import React from 'react';
import { authorityData } from '../data/mock';
import { Check } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const SHANTANU_IMAGE = 'https://customer-assets.emergentagent.com/job_presence-design-1/artifacts/4tzfolg9_edit11.jpg';

export const AuthoritySection = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.1 });
  const [imageRef, imageVisible] = useScrollAnimation({ threshold: 0.3 });

  return (
    <section className="bg-gradient-to-b from-[#0f1a2a] to-[#1a2435] py-24 lg:py-32 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-2 h-2 bg-[#5a7fa8] rounded-full opacity-30 float-slow" />
        <div className="absolute top-40 right-32 w-1 h-1 bg-[#c9a87c] rounded-full opacity-40 float-reverse" />
        <div className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-[#5a7fa8] rounded-full opacity-20 float" />
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.02]" style={{ 
          backgroundImage: 'linear-gradient(#5a7fa8 1px, transparent 1px), linear-gradient(90deg, #5a7fa8 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
      </div>
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column - Photo */}
          <div
            ref={imageRef}
            className={`lg:col-span-4 relative scale-in ${imageVisible ? 'is-visible' : ''}`}
          >
            <div className="relative max-w-sm mx-auto lg:mx-0">
              {/* Decorative frame elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#5a7fa8]/30" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#c9a87c]/30" />
              
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#5a7fa8]/20 to-[#c9a87c]/10 blur-3xl scale-110" />
              
              {/* Image container with gradient mask */}
              <div className="relative overflow-hidden rounded-sm">
                {/* The image with blend effect */}
                <div 
                  className="relative aspect-[3/4] overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #0f1a2a 0%, #1a2435 100%)'
                  }}
                >
                  <img 
                    src={SHANTANU_IMAGE}
                    alt="Shantanu Das Sharma - Leadership Architect"
                    className="w-full h-full object-cover object-top"
                    style={{
                      maskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%), linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                      maskComposite: 'intersect',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
                    }}
                  />
                  
                  {/* Overlay gradient for blending */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1a2a] via-transparent to-transparent opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0f1a2a]/50 via-transparent to-[#0f1a2a]/50" />
                  
                  {/* Subtle color overlay for cohesion */}
                  <div className="absolute inset-0 bg-[#0f1a2a]/10 mix-blend-overlay" />
                </div>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a87c]/50 to-transparent" />
              </div>
              
              {/* Floating accent dots */}
              <div className="absolute -top-2 right-8 w-1.5 h-1.5 bg-[#c9a87c] rounded-full opacity-60 float-slow" />
              <div className="absolute bottom-12 -left-2 w-1 h-1 bg-[#5a7fa8] rounded-full opacity-40 float-reverse" />
            </div>
          </div>

          {/* Middle Column - Name & Title */}
          <div
            ref={titleRef}
            className={`lg:col-span-4 slide-in-left ${titleVisible ? 'is-visible' : ''}`}
          >
            <p className="text-xs text-[#5a7fa8] uppercase tracking-[0.3em] mb-4">
              {authorityData.title}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#f5f5f0] leading-tight mb-8">
              {authorityData.name}
            </h2>
            
            {/* Decorative element */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gradient-to-r from-[#c9a87c] to-transparent" />
              <div className="w-2 h-2 rounded-full bg-[#c9a87c] opacity-60 breathe" />
            </div>
            
            {/* Philosophy - moved here for visual balance */}
            <div className="hidden lg:block">
              <p className="font-serif text-lg text-[#f5f5f0] leading-relaxed italic opacity-90 mb-3">
                "{authorityData.philosophy}"
              </p>
              <p className="text-sm text-[#c9a87c]">
                — {authorityData.quoteAuthor}
              </p>
              <p className="text-xs text-[#6b7c93]">
                {authorityData.quoteAuthorTitle}
              </p>
            </div>
          </div>

          {/* Right Column - Credentials */}
          <div 
            ref={contentRef}
            className={`lg:col-span-4 stagger-children ${contentVisible ? 'is-visible' : ''}`}
          >
            {/* Credentials */}
            <div className="space-y-5 mb-10">
              {authorityData.credentials.map((credential, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-6 h-6 rounded-full border border-[#5a7fa8]/50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:border-[#c9a87c] group-hover:bg-[#c9a87c]/10 transition-all duration-300">
                    <Check size={12} className="text-[#5a7fa8] group-hover:text-[#c9a87c] transition-colors duration-300" />
                  </div>
                  <p className="text-base text-[#b8c5d4] group-hover:text-[#f5f5f0] transition-colors duration-300">{credential}</p>
                </div>
              ))}
            </div>

            {/* Philosophy - mobile only */}
            <div className="lg:hidden pt-8 border-t border-[#2a3a4d] relative">
              <div className="absolute top-0 left-0 w-16 h-px bg-gradient-to-r from-[#5a7fa8] to-transparent" />
              <p className="font-serif text-xl text-[#f5f5f0] leading-relaxed italic mb-3">
                "{authorityData.philosophy}"
              </p>
              <p className="text-sm text-[#c9a87c]">
                — {authorityData.quoteAuthor}
              </p>
              <p className="text-xs text-[#6b7c93]">
                {authorityData.quoteAuthorTitle}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
